import React from 'react';
import Image from 'next/image';
import { PlayIcon } from '@heroicons/react/24/solid'; // For the play icon on video thumbnails
import { getImageVariants } from '@/lib/news_helpers';
import Link from 'next/link';
import type { FeedPreviewCard } from '@/lib/feed_preview';

interface NewsPreviewProps {
  newsObject: FeedPreviewCard;
  layout?: 'horizontal' | 'vertical'; // Optional prop for layout type
  // Set for the first above-the-fold cards only: next/image defaults to
  // loading="lazy", which hides the image from the preload scanner — and
  // these thumbnails are the LCP element on district/city/home pages
  // (Aug 2026 Lighthouse audit). Callers mark the few cards visible
  // without scrolling; everything below the fold should stay lazy.
  priority?: boolean;
}

const NewsPreview: React.FC<NewsPreviewProps> = ({ newsObject, layout, priority }) => {
  const { card_obj } = newsObject;
  const { title, share_image, thumbnail_url, data, by_line_with_link } =
    card_obj as NonNullable<FeedPreviewCard['card_obj']>;

  return (
    <Link
      data-id={newsObject.id}
      href={`/video/${newsObject.id}`}
      className='block w-full'
      prefetch={false}
    >
      {layout === 'horizontal' ? (
        <div className='flex w-full @container overflow-hidden hover:shadow-md transition-shadow duration-200'>
          <div className='relative w-1/3  @min-[400px]:w-32 h-20 @min-[400px]:h-24 bg-gray-200 flex-shrink-0 flex self-center items-center justify-center'>
            <Image
              className='h-full w-full'
              src={
                getImageVariants(
                  thumbnail_url || share_image || data.share_image_url,
                  'm',
                )[0]
              }
              alt={title || 'News video thumbnail'}
              width={150}
              height={100}
              // Rendered box is w-1/3 of the card on narrow containers and
              // a fixed 128px from the @min-[400px] breakpoint up.
              sizes='(max-width: 400px) 33vw, 128px'
              priority={priority}
              style={{ objectFit: 'cover' }}
            />
            <div className='absolute inset-0 flex items-center justify-center'>
              <div className='bg-opacity-30 p-2 bg-red-500 border-2 border-white rounded-full'>
                <PlayIcon className='h-4 w-4 text-white opacity-90' />
              </div>
            </div>
          </div>
          <div className='p-2 flex flex-col justify-center'>
            <h3 className='text-sm font-bold text-gray-800 line-clamp-3'>
              {title}
            </h3>
            <p className='text-xs text-gray-400'>{by_line_with_link}</p>
          </div>
        </div>
      ) : (
        <div className='flex-shrink-0 w-64 bg-white rounded-lg shadow-sm overflow-hidden border border-gray-200 hover:shadow-md transition-shadow duration-200'>
          <div className='relative w-full h-20 bg-gray-200 flex items-center justify-center'>
            <Image
              className='h-full w-full rounded-t-lg'
              src={getImageVariants(thumbnail_url || share_image, 'm')[0]}
              alt={title || 'News video thumbnail'}
              width={150}
              height={100}
              // Vertical cards are a fixed w-64 (256px) rail.
              sizes='256px'
              priority={priority}
              style={{ objectFit: 'cover' }}
            />
            <div className='absolute inset-0 flex items-center justify-center bg-opacity-30'>
              <div className='bg-red-500 border-2 border-white rounded-full p-2'>
                <PlayIcon className='h-5 w-5 text-white opacity-90' />
              </div>
            </div>
          </div>
          <div className='p-2'>
            <h3 className='text-sm font-bold text-gray-800 line-clamp-3 leading-tight'>
              {title}
            </h3>
            <p className='text-xs text-gray-400'>{by_line_with_link}</p>
          </div>
        </div>
      )}
    </Link>
  );
};

export default NewsPreview;
